import {
    Button,
    Dialog,
    DialogActions,
    DialogBody,
    DialogContent,
    DialogSurface,
    DialogTitle,
    Field,
    Textarea,
    MessageBar,
    Badge,
    Text,
    Spinner,
} from '@fluentui/react-components';
import { Dismiss24Regular } from '@fluentui/react-icons';
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { TextareaProps } from '@fluentui/react-components';
import { useBulkAddDialogStyles } from './BulkAddDialog.styles';

export interface BulkAddDialogResponseMessage {
    type: 'success' | 'error' | 'warning';
    message: string;
}

export interface BulkAddDialogProps<TResult> {
    open: boolean;
    title: string;
    fieldLabel: string;
    fieldHint: string;
    children?: ReactNode;
    footerContent?: ReactNode;
    processingLabel: string;
    saveButtonLabel: string;
    countBadgeLabel: (count: number) => string;
    duplicateItemsLabel: (count: number) => string;
    invalidItemsLabel: (count: number) => string;
    maxItems: number;
    itemPattern: RegExp;
    maxItemsErrorMessage: string;
    invalidMessage: string;
    isSubmitDisabled?: boolean;
    onClose: () => void;
    submitItems: (items: string[]) => Promise<TResult>;
    getResponseMessage: (result: TResult) => BulkAddDialogResponseMessage | null;
    renderResponseDetails: (result: TResult) => ReactNode;
    onSubmitted?: (result: TResult) => void;
}

const normalizeText = (value: string): string => value.replace(/&#x0a;/g, '\n');

const splitItems = (value: string): string[] =>
    normalizeText(value)
        .split(/[,;\n\t ]+/)
        .map((item) => item.trim())
        .filter(Boolean);

function BulkAddDialog<TResult>({
    open,
    title,
    fieldLabel,
    fieldHint,
    children,
    footerContent,
    processingLabel,
    saveButtonLabel,
    countBadgeLabel,
    duplicateItemsLabel,
    invalidItemsLabel,
    maxItems,
    itemPattern,
    maxItemsErrorMessage,
    invalidMessage,
    isSubmitDisabled,
    onClose,
    submitItems,
    getResponseMessage,
    renderResponseDetails,
    onSubmitted,
}: BulkAddDialogProps<TResult>) {
    const styles = useBulkAddDialogStyles();
    const [value, setValue] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [responseData, setResponseData] = useState<TResult | null>(null);
    const [responseMessage, setResponseMessage] = useState<BulkAddDialogResponseMessage | null>(null);

    useEffect(() => {
        if (!open) {
            setValue('');
            setResponseData(null);
            setResponseMessage(null);
            setIsLoading(false);
        }
    }, [open]);

    const parsed = useMemo(() => {
        const tokens = splitItems(value);
        const duplicates = tokens.filter((token, index) => tokens.indexOf(token) !== index);
        const unique = Array.from(new Set(tokens));
        const invalid = unique.filter((item) => !itemPattern.test(item));

        let error: string | undefined;
        if (unique.length === 0) {
            error = undefined;
        } else if (unique.length > maxItems) {
            error = maxItemsErrorMessage;
        } else if (invalid.length > 0) {
            error = invalidMessage;
        }

        return { tokens, unique, duplicates, invalid, error };
    }, [invalidMessage, itemPattern, maxItems, maxItemsErrorMessage, value]);

    const isValid = open && !parsed.error && parsed.unique.length > 0;

    const handleInputChange: TextareaProps['onChange'] = useCallback((_, data) => {
        setValue(data?.value ?? '');
    }, []);

    const handleRemoveItem = useCallback((item: string) => {
        setValue((currentValue) => {
            const nextItems = splitItems(currentValue).filter((token) => token !== item);
            return nextItems.join('\n');
        });
    }, []);

    const handleSaveClick = useCallback(async () => {
        if (!isValid) {
            return;
        }

        setIsLoading(true);
        setResponseMessage(null);
        setResponseData(null);

        try {
            const result = await submitItems(parsed.unique);

            setResponseData(result);
            setResponseMessage(getResponseMessage(result));
            onSubmitted?.(result);
        } catch (error) {
            setResponseMessage({
                type: 'error',
                message: error instanceof Error ? error.message : String(error),
            });
        } finally {
            setTimeout(() => {
                setIsLoading(false);
            }, 1000);
        }
    }, [getResponseMessage, isValid, onSubmitted, parsed.unique, submitItems]);

    return (
        <Dialog open={open}>
            <DialogSurface>
                <DialogBody>
                    <DialogTitle
                        action={
                            <Button
                                appearance="subtle"
                                aria-label="close"
                                icon={<Dismiss24Regular />}
                                onClick={onClose}
                            />
                        }
                    >
                        {title}
                    </DialogTitle>

                    <DialogContent>
                        {isLoading && (
                            <div className={styles.processingContainer}>
                                <Spinner label={processingLabel} />
                            </div>
                        )}

                        {!isLoading && responseMessage && (
                            <MessageBar intent={responseMessage.type}>
                                {responseMessage.message}
                            </MessageBar>
                        )}

                        {!isLoading && !responseData && (
                            <>
                                {children}

                                <Field
                                    label={fieldLabel}
                                    hint={fieldHint}
                                    required
                                    validationState={parsed.error ? 'error' : undefined}
                                    validationMessage={parsed.error}
                                >
                                    <Textarea
                                        appearance="outline"
                                        size="medium"
                                        resize="vertical"
                                        onChange={handleInputChange}
                                        value={value}
                                    />
                                </Field>

                                {parsed.unique.length > 0 && (
                                    <>
                                        <Badge className={styles.badgeCount} color="brand" size="medium">
                                            {countBadgeLabel(parsed.unique.length - parsed.invalid.length)}
                                        </Badge>
                                        <div className={styles.previewContainer}>
                                            {parsed.unique.map((item) => (
                                                <div key={item} className={styles.previewItem}>
                                                    <Text size={200}>{item}</Text>
                                                    <Button
                                                        appearance="subtle"
                                                        size="small"
                                                        aria-label={`Delete ${item}`}
                                                        icon={<Dismiss24Regular />}
                                                        onClick={() => handleRemoveItem(item)}
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    </>
                                )}

                                {parsed.duplicates.length > 0 && (
                                    <MessageBar className={styles.messageBar} intent="warning">
                                        {duplicateItemsLabel(parsed.duplicates.length)}
                                    </MessageBar>
                                )}

                                {parsed.invalid.length > 0 && (
                                    <MessageBar className={styles.messageBar} intent="error">
                                        {invalidItemsLabel(parsed.invalid.length)}
                                    </MessageBar>
                                )}
                            </>
                        )}

                        {!isLoading && responseData && (
                            <div className={styles.resultsContainer}>
                                {renderResponseDetails(responseData)}
                            </div>
                        )}
                    </DialogContent>

                    <DialogActions>
                        {!responseData && (
                            <Button
                                appearance="primary"
                                disabled={!isValid || isLoading || !!isSubmitDisabled}
                                onClick={handleSaveClick}
                            >
                                {saveButtonLabel}
                            </Button>
                        )}
                    </DialogActions>                  
                </DialogBody>
                {!isLoading && !responseData && footerContent && (
                    <div className={styles.footerContainer}>{footerContent}</div>
                )}
            </DialogSurface>
        </Dialog>
    );
}

export default BulkAddDialog;